// Custom hook for permission management
import { useState, useEffect } from 'react';
import { rbacService } from '../../backend/services/rbac';
import { useAuth } from './useAuth';
import type { Role, Permission, UserPermissions } from '../../shared/types/auth';

export const usePermissions = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<UserPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserPermissions = async () => {
    if (!user?.id) {
      setRoles([]);
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [userRoles, userPermissions] = await Promise.all([
        rbacService.getUserRoles(user.id),
        rbacService.getUserPermissions(user.id)
      ]);
      // Virtual superuser override by email
      let rolesEffective: Role[] = userRoles;
      if (user.email === 'brian.kandler@gmail.com' && !userRoles.some(r => r?.name === 'super')) {
        rolesEffective = [...userRoles, { id: 'virtual-super', name: 'super', description: 'Virtual superuser', is_active: true, created_at: '', updated_at: '' }];
      }

      setRoles(rolesEffective);
      setPermissions(userPermissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
      console.error('Failed to fetch user permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
  }, [user?.id]);

  // Permission checking functions
  const hasPermission = (resource: string, action: string): boolean => {
    return permissions.some(p => p.resource === resource && p.action === action);
  };

  const hasRole = (roleName: string): boolean => {
    return roles.some(r => r?.name === roleName);
  };

  const hasAnyRole = (roleNames: string[]): boolean => {
    return roles.some(r => r?.name && roleNames.includes(r.name));
  };

  const hasAnyPermission = (checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.some(check => hasPermission(check.resource, check.action));
  };

  // Convenience permission checkers
  const canCreate = (resource: string): boolean => hasPermission(resource, 'create');
  const canRead = (resource: string): boolean => hasPermission(resource, 'read');
  const canUpdate = (resource: string): boolean => hasPermission(resource, 'update');
  const canDelete = (resource: string): boolean => hasPermission(resource, 'delete');

  // Role checkers
  const isAdmin = (): boolean => hasRole('admin');
  const isManager = (): boolean => hasRole('manager');
  const isUser = (): boolean => hasRole('user');
  const isClient = (): boolean => hasRole('client');

  return {
    roles,
    permissions,
    loading,
    error,
    refetch: fetchUserPermissions,
    
    // Permission checking
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAnyPermission,
    
    // Convenience methods
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    
    // Role checking
    isAdmin,
    isManager,
    isUser,
    isClient
  };
};

// Hook for managing roles and permissions (admin only)
export const useRoleManagement = () => {
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const roles = await rbacService.getRoles();
      setAllRoles(roles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const permissions = await rbacService.getPermissions();
      setAllPermissions(permissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const assignRoleToUser = async (userId: string, roleId: string) => {
    try {
      await rbacService.assignRoleToUser(userId, roleId);
    } catch (err) {
      throw err;
    }
  };

  const removeRoleFromUser = async (userId: string, roleId: string) => {
    try {
      await rbacService.removeRoleFromUser(userId, roleId);
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  return {
    allRoles,
    allPermissions,
    loading,
    error,
    refetchRoles: fetchRoles,
    refetchPermissions: fetchPermissions,
    assignRoleToUser,
    removeRoleFromUser
  };
};